"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Target, MapPin, ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Zap, Building2, ShieldCheck, Compass } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const marketMetrics = [{
  id: 1,
  title: "Regional Search Growth",
  value: "+38%",
  description: "YoY increase in high-intent commercial searches in Carlisle & Cumbria.",
  icon: "TrendingUp"
}, {
  id: 2,
  title: "Map Pack Vulnerability",
  value: "62%",
  description: "Of top-ranking Carlisle businesses lack optimized local schema & citation trust.",
  icon: "MapPin"
}, {
  id: 3,
  title: "Commercial Intent",
  value: "74%",
  description: "Of local search queries carry immediate buying or service-inquiry intent.",
  icon: "Target"
}, {
  id: 4,
  title: "Average Organic Gap",
  value: "4.2x",
  description: "Traffic multiplier achieved when ranking in top 3 for Carlisle localized terms.",
  icon: "BarChart3"
}];
const carlisleKeywords = [{
  id: 1,
  keyword: "Carlisle professional services",
  volume: "High Intent",
  competition: "Moderate",
  opportunity: "First-page gap in technical authority & localized content clusters."
}, {
  id: 2,
  keyword: "B2B suppliers Cumbria",
  volume: "High Intent",
  competition: "Low-Medium",
  opportunity: "Strong demand for direct supplier procurement with low organic competition."
}, {
  id: 3,
  keyword: "Commercial contractors Carlisle",
  volume: "Medium-High",
  competition: "Moderate",
  opportunity: "Local map pack and review schema provide immediate rank elevation."
}, {
  id: 4,
  keyword: "Industrial & logistics Carlisle",
  volume: "High Value",
  competition: "Low",
  opportunity: "High revenue per lead with minimal content depth from competitors."
}];
const strategicPillars = [{
  id: 1,
  title: "Google Map Pack & Entity Authority",
  description: "I optimize your Google Business Profile and establish Carlisle geographic entities so search engines associate your brand directly with local service intent."
}, {
  id: 2,
  title: "Carlisle Content Cluster Architecture",
  description: "I construct deep, locally relevant content hubs targeting commercial keywords that out-rank thin regional landing pages."
}, {
  id: 3,
  title: "Cumbria & Northern Link Ecosystems",
  description: "I earn high-trust regional backlinks and citations from genuine Northern UK business directories and trade publications."
}, {
  id: 4,
  title: "Technical & Core Web Vitals Optimization",
  description: "I streamline your site speed, mobile rendering, and structured data so Google seamlessly indexes your Carlisle landing pages."
}];
const neighboringCities = [{
  id: 1,
  name: "Preston",
  county: "Lancashire",
  description: "Key commercial hub offering high-intent regional growth.",
  urlPath: "/LocalSeoConsultantPrestonPage"
}, {
  id: 2,
  name: "Sunderland",
  county: "Tyne & Wear",
  description: "Industrial and coastal market with expanding search volume.",
  urlPath: "/LocalSeoConsultantSunderlandPage"
}, {
  id: 3,
  name: "Bolton",
  county: "Greater Manchester",
  description: "Competitive market requiring tailored local search strategies.",
  urlPath: "/LocalSeoConsultantBoltonPage"
}, {
  id: 4,
  name: "Wigan",
  county: "Greater Manchester",
  description: "High-density local service market with strong search demand.",
  urlPath: "/LocalSeoConsultantWiganPage"
}, {
  id: 5,
  name: "Doncaster",
  county: "South Yorkshire",
  description: "Strategic logistics hub with high commercial search value.",
  urlPath: "/LocalSeoConsultantDoncasterPage"
}, {
  id: 6,
  name: "Warrington",
  county: "Cheshire",
  description: "Major distribution and B2B market in the North West.",
  urlPath: "/LocalSeoConsultantWarringtonPage"
}];
export default function CarlisleMarketAnalysis() {
  const shouldReduceMotion = useReducedMotion();
  const renderMetricIcon = (iconName: string) => {
    switch (iconName) {
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary" />;
      case "MapPin":
        return <MapPin className="size-5 text-primary" />;
      case "Target":
        return <Target className="size-5 text-primary" />;
      case "BarChart3":
        return <BarChart3 className="size-5 text-primary" />;
      default:
        return <Search className="size-5 text-primary" />;
    }
  };
  return <section id="carlislemarketanalysis" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="aria-hidden:hidden pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/30 blur-3xl opacity-50" />
      <div className="aria-hidden:hidden pointer-events-none absolute bottom-0 -left-24 size-96 rounded-full bg-accent/10 blur-3xl opacity-50" />

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-14 md:mb-20">
          <Badge variant="secondary" className="px-3 py-1 text-xs md:text-sm font-medium tracking-wide">
            <Compass className="size-3.5 mr-1.5 text-primary" />
            Carlisle & Cumbria SEO Intelligence
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Carlisle Local Search Trends & Growth Opportunities
          </h2>

          <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
            As an independent SEO consultant, I personally conduct granular search landscape analysis for Carlisle and surrounding Cumbria businesses. When you work with me, you get direct expert strategy—never delegated agency templates or generic automation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {marketMetrics.map((metric, i) => <motion.div key={metric.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }}>
              <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-2.5 rounded-lg bg-muted flex items-center justify-center">
                      {renderMetricIcon(metric.icon)}
                    </div>
                    <span className="text-2xl md:text-3xl font-bold font-default text-primary tracking-tight">
                      {metric.value}
                    </span>
                  </div>
                  <CardTitle className="text-lg font-semibold font-default text-card-foreground">
                    {metric.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed font-default">
                    {metric.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-20">
          <div className="lg:col-span-7 flex flex-col">
            <Card className="h-full border border-border bg-card shadow-xs flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Search className="size-5 text-primary" />
                  <CardTitle className="text-xl md:text-2xl font-semibold font-default">
                    High-Intent Carlisle Search Demand
                  </CardTitle>
                </div>
                <CardDescription className="text-muted-foreground text-sm font-default">
                  Analyzed query verticals offering immediate commercial customer capture across Carlisle and North Cumbria.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 grow">
                {carlisleKeywords.map((kw, i) => <div key={kw.id} data-index={i} className="p-4 rounded-lg bg-background border border-border transition-[border-color,box-shadow] hover:border-primary/30">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-semibold text-foreground text-sm md:text-base font-default">
                        {kw.keyword}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs font-medium">
                          {kw.volume}
                        </Badge>
                        <Badge variant="secondary" className="text-xs font-medium bg-muted text-foreground">
                          Comp: {kw.competition}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground font-default leading-relaxed">
                      {kw.opportunity}
                    </p>
                  </div>)}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-5 flex flex-col">
            <Card className="h-full border border-border bg-card shadow-xs flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="size-5 text-primary" />
                  <CardTitle className="text-xl md:text-2xl font-semibold font-default">
                    My Carlisle Growth Strategy
                  </CardTitle>
                </div>
                <CardDescription className="text-muted-foreground text-sm font-default">
                  How I position Carlisle businesses to secure top organic rankings and convert local search traffic.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 grow">
                {strategicPillars.map((pillar, i) => <div key={pillar.id} data-index={i} className="flex items-start gap-3">
                    <div className="mt-1 shrink-0 p-1 rounded-full bg-primary/10 text-primary">
                      <CheckCircle2 className="size-4" />
                    </div>
                    <div>
                      <h3 className="text-sm md:text-base font-semibold font-default text-card-foreground">
                        {pillar.title}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground font-default leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-muted/40 p-6 md:p-10 mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="size-5 text-primary" />
                <h3 className="text-2xl font-semibold font-default text-foreground">
                  Regional SEO Coverage Beyond Carlisle
                </h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground font-default">
                Expanding your service footprint across the North West and neighbouring regions? Explore my tailored local SEO strategies for surrounding commercial markets.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <div key={city.id} data-index={i}>
                <Link to={city.urlPath} className="group block p-4 rounded-xl border border-border bg-card transition-[border-color,box-shadow,transform] hover:border-primary/50 hover:shadow-sm">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <MapPin className="size-4 text-primary shrink-0" />
                      <span className="font-semibold text-foreground group-hover:text-primary transition-colors font-default">
                        {city.name}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-[11.5px] px-1.5 py-0">
                      {city.county}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 font-default mb-3">
                    {city.description}
                  </p>
                  <div className="flex items-center text-xs font-medium text-primary group-hover:underline">
                    <span>View Local SEO Strategy</span>
                    <ArrowRight className="size-3 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </div>)}
          </div>
        </div>

        <div className="relative rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 overflow-hidden shadow-xl">
          <div className="aria-hidden:hidden pointer-events-none absolute -right-16 -bottom-16 size-72 rounded-full bg-accent/20 blur-2xl" />

          <div className="relative z-10 max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3 py-1 text-xs font-medium backdrop-blur-xs">
              <ShieldCheck className="size-3.5" />
              Direct Consultant Access • Yassine Essaadi
            </div>

            <h3 className="text-xl sm:text-2xl font-bold font-default tracking-tight text-primary-foreground">
              Ready to Win Higher Organic Rankings in Carlisle?
            </h3>

            <p className="text-sm md:text-base text-primary-foreground/90 font-default leading-relaxed">
              Let me personally audit your Carlisle search visibility, identify technical bottlenecks, and map out a custom strategy built for sustainable search growth. You will work directly with me every step of the way.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button size="lg" variant="secondary" asChild className="font-semibold">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/CaseStudiesPage">
                  Explore Genuine Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}